package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import dev.lingvo.shared.ShotNotificationPreferencesDto
import dev.lingvo.shared.ShotNotificationWorkspacePreferencesDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.WebhookEvent
import dev.lingvo.shared.screenshotNotificationEvents
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.WorkspaceSelection
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** A failed read never offers unchecked controls that could overwrite a member's existing choices. */
@Composable
internal fun ScreenshotEmailPreferences(
    slug: String, subject: ProjectAuthoritySubject?, platform: StorePlatform, runner: ScreenshotUpdatesRunner,
) {
    var reload by remember { mutableStateOf(0) }
    val loaded = loadReloadable(subject, platform, reloadToken = reload) {
        readScreenshotUpdates(subject, slug) { route ->
            val orgSlug = route.workspace.workspace.slug
            orgSlug to ScreenshotUpdatesApi.personal(orgSlug)
        }
    }
    LSectionHeading(t("app.projects.slug.screenshots.updates_preferences.your_email_notifications", "Your email notifications"),
        t("app.projects.slug.screenshots.updates_preferences.opt_in_to_events_across_every", "Opt in to events across every project and platform in this workspace. Clear all choices and save to opt out."))
    when (val state = loaded.state) {
        Async.Loading -> LoadingState(rows = 3)
        is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.updates_preferences.your_email_preferences", "your email preferences"), onRetry = { reload++ })
        is Async.Success -> key(state.value.first) { PersonalEmailEditor(state.value.first, state.value.second, runner) }
    }
}

@Composable
private fun PersonalEmailEditor(orgSlug: String, initial: ShotNotificationPreferencesDto, runner: ScreenshotUpdatesRunner) {
    var events by remember { mutableStateOf(initial.emailEvents.toSet()) }
    var busy by remember { mutableStateOf(false) }
    var failure by remember { mutableStateOf<String?>(null) }
    var saved by remember { mutableStateOf(false) }
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LMuted(tf("screenshots.updates.workspace_scope", "Workspace: {workspace}", "workspace" to orgSlug))
            ScreenshotEventChoices(events, !busy) { events = it; saved = false }
            LMuted(t("app.projects.slug.screenshots.updates_preferences.an_owner_or_admin_can_pause", "An owner or admin can pause workspace email delivery. Your subscriptions stay saved while delivery is paused."))
            failure?.let { LAlert(it) }
            PreferenceSaved(saved)
            LButton(t("app.projects.slug.screenshots.updates_preferences.save_email_preferences", "Save email preferences"), {
                runner.run(ScreenshotUpdatesAction.PERSONAL_EMAIL, { busy = it }, {
                    ScreenshotUpdatesApi.savePersonal(orgSlug, ShotNotificationPreferencesDto(screenshotNotificationEvents.filter { it in events }))
                }, { failure = shotRequestFailure(it); saved = false }, {
                    events = it.emailEvents.toSet(); failure = null; saved = true
                })
            }, enabled = !busy, loading = busy, variant = LButtonVariant.SECONDARY, modifier = updatesControlModifier())
        }
    }
}

@Composable
internal fun ScreenshotWorkspaceNotifications(
    slug: String, subject: ProjectAuthoritySubject?, platform: StorePlatform, runner: ScreenshotUpdatesRunner,
) {
    var reload by remember { mutableStateOf(0) }
    val loaded = loadReloadable(subject, platform, reloadToken = reload) {
        readScreenshotUpdates(subject, slug) { route ->
            val orgSlug = route.workspace.workspace.slug
            orgSlug to ScreenshotUpdatesApi.workspace(orgSlug)
        }
    }
    LSectionHeading(t("app.projects.slug.screenshots.updates_preferences.workspace_delivery", "Workspace delivery"),
        t("app.projects.slug.screenshots.updates_preferences.these_controls_apply_to_all_workspace", "These controls apply to all workspace members, projects and screenshot platforms."))
    when (val state = loaded.state) {
        Async.Loading -> LoadingState(rows = 3)
        is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.updates_preferences.workspace_delivery_preferences", "workspace delivery preferences"), onRetry = { reload++ })
        is Async.Success -> key(state.value.first) { WorkspaceNotificationsEditor(state.value.first, state.value.second, runner) }
    }
}

@Composable
private fun WorkspaceNotificationsEditor(
    orgSlug: String, initial: ShotNotificationWorkspacePreferencesDto, runner: ScreenshotUpdatesRunner,
) {
    var emailEnabled by remember { mutableStateOf(initial.emailEnabled) }
    var events by remember { mutableStateOf(initial.webhookEvents.toSet()) }
    var busy by remember { mutableStateOf(false) }
    var failure by remember { mutableStateOf<String?>(null) }
    var saved by remember { mutableStateOf(false) }
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LMuted(tf("screenshots.updates.workspace_scope", "Workspace: {workspace}", "workspace" to orgSlug))
            LCheckbox(emailEnabled, { emailEnabled = it; saved = false },
                t("app.projects.slug.screenshots.updates_preferences.allow_screenshot_emails_for_members_who", "Allow screenshot emails for members who opt in"), enabled = !busy)
            LSectionHeading(t("app.projects.slug.screenshots.updates_preferences.screenshot_webhook_events", "Screenshot webhook events"),
                t("app.projects.slug.screenshots.updates_preferences.both_these_choices_and_an_existing", "Both these choices and an existing endpoint subscription must allow an event before it is delivered."))
            ScreenshotEventChoices(events, !busy) { events = it; saved = false }
            LLinkButton(t("app.projects.slug.screenshots.updates_preferences.configure_workspace_webhooks", "Configure workspace webhooks"), "/app/settings/webhooks",
                modifier = updatesControlModifier().onClick { WorkspaceSelection.select(orgSlug) }, variant = LButtonVariant.GHOST)
            failure?.let { LAlert(it) }
            PreferenceSaved(saved)
            LButton(t("app.projects.slug.screenshots.updates_preferences.save_workspace_delivery", "Save workspace delivery"), {
                runner.run(ScreenshotUpdatesAction.WORKSPACE_NOTIFICATIONS, { busy = it }, {
                    ScreenshotUpdatesApi.saveWorkspace(orgSlug, ShotNotificationWorkspacePreferencesDto(emailEnabled,
                        screenshotNotificationEvents.filter { it in events }))
                }, { failure = shotRequestFailure(it); saved = false }, {
                    emailEnabled = it.emailEnabled; events = it.webhookEvents.toSet(); failure = null; saved = true
                })
            }, enabled = !busy, loading = busy, variant = LButtonVariant.SECONDARY, modifier = updatesControlModifier())
        }
    }
}

/** Shared fixed event rows keep email and webhook controls aligned with the additive server enum. */
@Composable
private fun ScreenshotEventChoices(selected: Set<WebhookEvent>, enabled: Boolean, onSelected: (Set<WebhookEvent>) -> Unit) {
    screenshotNotificationEvents.forEach { event ->
        val label = when (event) {
            WebhookEvent.SCREENSHOT_CAPTURE_CURRENT -> t("app.projects.slug.screenshots.updates_preferences.capture_becomes_current", "Capture becomes current")
            WebhookEvent.SCREENSHOT_CAPTURE_STALE -> t("app.projects.slug.screenshots.updates_preferences.capture_becomes_stale", "Capture becomes stale")
            WebhookEvent.SCREENSHOT_SET_APPROVED -> t("app.projects.slug.screenshots.updates_preferences.screenshot_set_approved", "Screenshot set approved")
            WebhookEvent.SCREENSHOT_PUSH_SUCCEEDED -> t("app.projects.slug.screenshots.updates_preferences.store_push_succeeded", "Store push succeeded")
            WebhookEvent.SCREENSHOT_PUSH_FAILED -> t("app.projects.slug.screenshots.updates_preferences.store_push_failed_or_partly_completed", "Store push failed or partly completed")
            WebhookEvent.SCREENSHOT_SNAPSHOT_NAMED -> t("app.projects.slug.screenshots.updates_preferences.design_snapshot_named", "Design snapshot named")
            else -> return@forEach
        }
        LCheckbox(event in selected, { checked -> onSelected(if (checked) selected + event else selected - event) },
            label, enabled = enabled, modifier = Modifier.fillMaxWidth().styleModifier { property("min-height", "40px") })
    }
}

@Composable
private fun PreferenceSaved(saved: Boolean) {
    Div(attrs = { attr("role", "status"); attr("aria-live", "polite") }) {
        if (saved) Text(t("app.projects.slug.screenshots.updates_preferences.preferences_saved", "Preferences saved."))
    }
}

internal fun updatesControlModifier(): Modifier = Modifier.styleModifier {
    property("max-width", "100%"); property("min-height", "40px"); property("white-space", "normal")
}
